import type { TFunction } from 'i18next';
import { formatUnit } from '../../utils/units';
import { formatDateEuropean } from '../../utils/dateUtils';
import { formatLocalizedNumber, formatLocalizedQuantity } from '../../utils/numberFormatting';
import { CARGO_QUANTITY_TOLERANCE } from '../../utils/transportCargoAllocation';
import { getOrderProductDisplayDimensions } from '../orders/orderProductDisplay';

/**
 * operationalExceptionText.ts — single home for the human copy of every
 * operational-exception / consistency warning (owner decision 2026-09-21).
 *
 * Rules that must not be broken here:
 *  - a warning code is NEVER shown as the sentence an operator reads. An
 *    unknown code falls back to the backend's Polish `message`, and only then
 *    to a neutral Polish sentence; the raw code may appear solely as the small
 *    grey support caption (`supportCode`).
 *  - one warning = one short Polish paragraph, "what happens and why", written
 *    for a warehouse operator: no English, no enums, no SKUs.
 *  - consistency warnings (decision 2026-09-18) carry quantities, purchase
 *    prices and document numbers only — never customer sale prices or margin.
 *
 * Both readers of this copy use this module: the confirmation dialog
 * (`operationalExceptionFlow.tsx`) and the Rozbieżności registry
 * (`pages/OperationalExceptions.tsx`), so the register shows the very sentence
 * the operator confirmed.
 */

export interface OperationalWarning {
  code: string;
  /** Polish sentence produced by the backend; the fallback for unknown codes. */
  message?: string;
  details?: Record<string, any>;
}

export interface OperationalWarningCopy {
  /** The Polish sentence an operator reads. Never a bare code. */
  text: string;
  /** Set only when the sentence could not be derived from the code. */
  supportCode: string | null;
}

const WARNING_KEY = 'common:operationalException.warnings';

/**
 * Which order line an over-allocation names (plan 2026-10-13): the six lines of
 * one ZK can share a product name, so the warning says „ZK 1/02/2026 poz. 4:”
 * and the product with its dimensions. Older warnings without these fields
 * keep the bare product name.
 */
function overflowLineLabel(row: Record<string, unknown>, documentLabel: string | null, t: TFunction) {
  const lineNumber = Number(row.lineNumber);
  const prefix = documentLabel && lineNumber > 0
    ? `${String(t(`${WARNING_KEY}.overflowLine`, { document: documentLabel, line: lineNumber }))}: `
    : '';
  const dimensions = getOrderProductDisplayDimensions({
    width: row.width as number | undefined,
    height: row.height as number | undefined,
    length: row.length as number | undefined,
    thickness: row.thickness as number | undefined,
  } as Parameters<typeof getOrderProductDisplayDimensions>[0]);
  const name = String(row.productName || '');
  return { prefix, productName: dimensions ? `${name} ${dimensions}` : name };
}

/**
 * Every warning code the backend can confirm, in the order the Rozbieżności
 * filter lists them. It lives beside the copy so a new code cannot become
 * filterable without a Polish sentence; the WZ aliases below share one
 * sentence and are not separate filter entries.
 */
export const OPERATIONAL_EXCEPTION_CODES = [
  'WZ_QUANTITY_DIFFERS_FROM_ORDER',
  'PURCHASE_PRICE_DEVIATES',
  'WZ_VARIANT_DIFFERS_FROM_ORDER',
  // Decision 2026-10-04: batches picked on the WZ instead of FIFO.
  'WZ_BATCHES_NOT_FIFO',
  'DELIVERED_WITHOUT_RELEASED_WZ',
  'DELIVERY_INVOICE_QUANTITY_EXCEEDED',
  'PZ_QUANTITY_RECEIVED_EXCEEDS_ORDERED',
  'PZ_INVOICE_DATE_AFTER_RECEIPT',
  'PZ_INVOICE_NET_AMOUNT_MISMATCH',
  'CARGO_OVER_ALLOCATION',
  // Addendum A (decision 2026-09-30): ZD goods on several trucks.
  'SUPPLIER_CARGO_OVER_ALLOCATED',
  'SUPPLIER_LINE_BELOW_TRUCK_CARGO',
  'TRANSPORT_DELIVERY_EXCEEDS_PLANNED',
  'TRANSPORT_ACTUAL_DELIVERY_BEFORE_LOADING',
  'WZ_MISSING_PURCHASE_INVOICE',
  // WP1 "niczego nie blokujemy" (decision 2026-09-24): former locks.
  'WZ_CORRECTED_AFTER_INVOICE',
  'SUPPLIER_ORDER_PZ_QUANTITY_REDUCED',
  // WP1 part 2: a ZK / ZD changed after its approved invoice.
  'ORDER_CHANGED_AFTER_INVOICE',
  'SUPPLIER_ORDER_CHANGED_AFTER_INVOICE',
  // WP2: automatic entry, stock issued beyond the invoiced quantity.
  'ORDER_ISSUED_OVER_INVOICE',
  // WP12 "dopuśćmy to" (owner decision 2026-09-24): former ZD/PZ/transport locks.
  'SUPPLIER_ORDER_DELETED_WITH_DOCUMENTS',
  'TRANSPORT_WAREHOUSE_LINKS_REMOVED',
  'PZ_RECEIVED_WITHOUT_PURCHASE_INVOICE',
  'PZ_SUPPLIER_ORDER_TRACE_BROKEN',
  'PZ_TRANSPORT_ADDED_TO_SUPPLIER_ORDER',
  // WP14: a truck's direction disagrees with what it carries.
  'TRANSPORT_OUTBOUND_WITH_SUPPLIER_ORDERS',
  'TRANSPORT_INBOUND_WITHOUT_SUPPLIER_ORDERS',
  'TRANSPORT_CUSTOMER_PICKUP_ORDER',
  // WP12 part B1 (ZK, migration 191).
  'ORDER_LINE_WITHDRAWN',
  'SUPPLIER_ORDER_LINE_WITHDRAWN',
  'ORDER_LINES_REMOVED_FROM_TRANSPORT',
  'ORDER_DELETED_FROM_TRANSPORTS',
  // Owner 2026-10-14: a deleted/cancelled ZD and a cancelled ZK leave their trucks.
  'SUPPLIER_ORDER_REMOVED_FROM_TRANSPORTS',
  'ORDER_CANCELLED_WITH_LINKS',
  'ORDER_ARCHIVED',
  'SUPPLIER_ORDER_ARCHIVED',
  // WP15: complaint corrections (automatic, closed by the correction).
  'ORDER_COMPLAINT',
  'SUPPLIER_ORDER_COMPLAINT',
] as const;

const quantity = (value: unknown) => formatLocalizedQuantity(Number(value) || 0, 'pl');

/** Money always reads with two Polish decimals: 33,10 — never 33.1. */
const money = (value: unknown) => formatLocalizedNumber(Number(value) || 0, 2, 'pl');

/**
 * Percent without a decimal that carries no information: 55%, and 7,5% only
 * where a tenth of a percent still changes the reading (a small threshold).
 */
const percent = (value: unknown) => {
  const numeric = Number(value) || 0;
  const rounded = Math.round(numeric);
  const decimalMatters = Math.abs(numeric) < 10 && Math.abs(numeric - rounded) >= 0.05;
  return decimalMatters
    ? formatLocalizedNumber(numeric, 1, 'pl')
    : formatLocalizedNumber(rounded, 0, 'pl');
};

const unit = (value: unknown) => formatUnit(typeof value === 'string' ? value : null, 'pl');

/**
 * Polish unit labels end with their own period (`szt.`), so a sentence that
 * closes on a unit would read "226 szt..". The sentence keeps its full stop in
 * the dictionary; the duplicate is removed here, once, for every code.
 */
const finishSentence = (text: string) => text.replace(/\.\.(?=$|\s)/g, '.');

/**
 * A backend `message` is only usable as operator copy when it is a sentence.
 * An ALL_CAPS_CODE echoed as the message would reintroduce exactly the bug
 * this module exists to prevent.
 */
function isReadableSentence(message: unknown): message is string {
  const text = typeof message === 'string' ? message.trim() : '';
  if (text.length < 8 || !text.includes(' ')) return false;
  return !/^[A-Z0-9_ ]+$/.test(text);
}

/**
 * "what changes" list for the after-invoice warnings: field labels and line
 * names only (the backend never sends the new values, so no sale price leaks).
 */
function afterInvoiceChanges(details: Record<string, any>, t: TFunction): string {
  const changes = Array.isArray(details.changes) ? details.changes : [];
  const fieldList = (fields: unknown) => (Array.isArray(fields) ? fields : [])
    .map((field) => String(t(`${WARNING_KEY}.afterInvoiceFields.${field}`, { defaultValue: String(t(`${WARNING_KEY}.afterInvoiceFields.other`)) })))
    .filter((label, index, labels) => labels.indexOf(label) === index)
    .join(', ');
  const parts = changes.map((change: Record<string, any>) => {
    if (change.scope === 'HEADER') {
      return String(t(`${WARNING_KEY}.afterInvoiceHeader`, { fields: fieldList(change.fields) }));
    }
    const productName = change.name || String(t(`${WARNING_KEY}.afterInvoiceUnnamedLine`));
    if (change.change === 'ADDED') return String(t(`${WARNING_KEY}.afterInvoiceLineAdded`, { productName }));
    if (change.change === 'REMOVED') return String(t(`${WARNING_KEY}.afterInvoiceLineRemoved`, { productName }));
    return String(t(`${WARNING_KEY}.afterInvoiceLineUpdated`, { productName, fields: fieldList(change.fields) }));
  });
  return parts.join('; ');
}

function afterInvoiceNumbers(details: Record<string, any>, t: TFunction): string {
  const invoiceNumbers = Array.isArray(details.invoiceNumbers) ? details.invoiceNumbers.filter(Boolean) : [];
  return invoiceNumbers.length > 0
    ? String(t(`${WARNING_KEY}.afterInvoiceNumbers`, { invoiceNumbers: invoiceNumbers.join(', ') }))
    : '';
}

function sentenceForCode(warning: OperationalWarning, t: TFunction): string | null {
  const details = warning.details || {};
  switch (warning.code) {
    case 'WZ_QUANTITY_DIFFERS_FROM_ORDER': {
      const alreadyReleased = Number(details.alreadyReleasedQuantity) || 0;
      return String(t(`${WARNING_KEY}.wzQuantityDiffersFromOrder`, {
        productName: details.productName,
        unit: unit(details.unit),
        issuedQuantity: quantity(details.issuedQuantity),
        orderQuantity: quantity(details.orderQuantity),
        breakdown: alreadyReleased > 0
          ? String(t(`${WARNING_KEY}.wzQuantityDiffersFromOrderBreakdown`, {
              unit: unit(details.unit),
              wzQuantity: quantity(details.wzQuantity),
              alreadyReleasedQuantity: quantity(alreadyReleased),
            }))
          : '',
      }));
    }
    case 'WZ_BATCHES_NOT_FIFO': {
      const lines = Array.isArray(details.lines) ? details.lines : [];
      const describe = (parts: unknown, lineUnit: unknown) => (Array.isArray(parts) ? parts : [])
        .map((part: Record<string, any>) => `${part.pzNumber || '—'} ${quantity(part.quantity)} ${unit(lineUnit)}`)
        .join(', ');
      const list = lines.map((line: Record<string, any>) => String(t(`${WARNING_KEY}.wzBatchesNotFifoLine`, {
        productName: line.productName || '—',
        picked: describe(line.picked, line.unit) || '—',
        fifo: describe(line.fifo, line.unit) || '—',
      }))).join('; ');
      return String(t(`${WARNING_KEY}.wzBatchesNotFifo`, { lines: list }));
    }
    case 'WZ_VARIANT_DIFFERS_FROM_ORDER':
      return String(t(`${WARNING_KEY}.wzVariantDiffersFromOrder`, {
        productName: details.productName,
        wzProductName: details.wzProductName || details.productName,
      }));
    case 'PURCHASE_PRICE_DEVIATES': {
      const unitCost = Number(details.unitCost) || 0;
      const medianUnitCost = Number(details.medianUnitCost) || 0;
      return String(t(`${WARNING_KEY}.purchasePriceDeviates`, {
        productName: details.productName,
        currency: details.currency,
        pricingUnit: unit(details.pricingUnit),
        unitCost: money(unitCost),
        medianUnitCost: money(medianUnitCost),
        deviationPercent: percent(details.deviationPercent),
        comparison: String(t(unitCost >= medianUnitCost
          ? `${WARNING_KEY}.purchasePriceHigher`
          : `${WARNING_KEY}.purchasePriceLower`)),
      }));
    }
    case 'DELIVERED_WITHOUT_RELEASED_WZ': {
      const wzNumbers = Array.isArray(details.wzNumbers) ? details.wzNumbers : [];
      return String(t(`${WARNING_KEY}.deliveredWithoutReleasedWZ`, {
        orderNumber: details.orderNumber || '',
        wzNumbersSuffix: wzNumbers.length > 0
          ? String(t(`${WARNING_KEY}.deliveredWithoutReleasedWZDocuments`, { wzNumbers: wzNumbers.join(', ') }))
          : '',
      }));
    }
    case 'ORDER_READY_WITH_MISSING_GOODS': {
      const lines = Array.isArray(details.lines) ? details.lines : [];
      return String(t(`${WARNING_KEY}.orderReadyWithMissingGoods`, {
        orderNumber: details.orderNumber || '',
        lines: lines
          .map((line: Record<string, unknown>) => `${line.name || '—'} ${quantity(line.quantity)} ${unit(line.unit)}`)
          .join(', '),
      }));
    }
    case 'WZ_MISSING_PURCHASE_INVOICE':
    case 'WZ_BEFORE_PURCHASE_INVOICE':
    case 'WZ_PENDING_STOCK_ISSUE':
      return String(t(`${WARNING_KEY}.wzBeforePurchaseInvoice`));
    // Decision 2026-10-27: one document approved as parts on several orders.
    case 'INVOICE_DOCUMENT_PART': {
      const parts = Array.isArray(details.parts) ? details.parts : [];
      return String(t(`${WARNING_KEY}.invoiceDocumentPart`, {
        documentNumber: details.documentNumber || '',
        parts: parts
          .map((part: Record<string, unknown>) => `${part.orderNumber || '—'} (${[money(part.subtotalNet), part.currency].filter(Boolean).join(' ')})`)
          .join(', '),
      }));
    }
    case 'INVOICE_DOCUMENT_PARTS_EXCEED_TOTAL':
      // Without other parts the invoice alone is above the document net.
      return String(t(`${WARNING_KEY}.${Number(details.otherParts) === 0 ? 'invoiceAboveDocumentTotal' : 'invoiceDocumentPartsExceedTotal'}`, {
        documentNumber: details.documentNumber || '',
        partsNet: money(details.partsNet),
        documentNetTotal: money(details.documentNetTotal),
        difference: money(details.difference),
        currency: details.currency || '',
      }));
    case 'DELIVERY_INVOICE_QUANTITY_EXCEEDED':
      return String(t(`${WARNING_KEY}.deliveryInvoiceQuantityExceeded`, {
        productName: details.productName, unit: unit(details.unit),
        attempted: quantity(details.attempted), remaining: quantity(details.remaining),
        invoiced: quantity(details.invoiced),
      }));
    case 'PZ_QUANTITY_RECEIVED_EXCEEDS_ORDERED':
      return String(t(`${WARNING_KEY}.pzOverReceipt`, {
        productName: details.productName,
        unit: unit(details.unit),
        quantityReceived: quantity(details.quantityReceived),
        quantityOrdered: quantity(details.quantityOrdered),
        difference: quantity(Math.abs(Number(details.difference) || 0)),
      }));
    case 'PZ_INVOICE_DATE_AFTER_RECEIPT':
      return String(t(`${WARNING_KEY}.pzInvoiceDateAfterReceipt`, {
        invoiceDate: formatDateEuropean(details.invoiceDate),
        receiptDate: formatDateEuropean(details.receiptDate),
      }));
    case 'PZ_INVOICE_NET_AMOUNT_MISMATCH':
      return String(t(`${WARNING_KEY}.pzInvoiceNetAmountMismatch`, {
        productName: details.productName,
        lineValue: money(details.lineValue),
        netAmount: money(details.netAmount),
        currency: details.currency || '',
      }));
    case 'CARGO_OVER_ALLOCATION': {
      const rows = Array.isArray(details.overflow) ? details.overflow : [];
      if (rows.length === 0) return null;
      return rows.map((row: Record<string, unknown>) => {
        const line = overflowLineLabel(row, row.orderNumber ? `ZK ${row.orderNumber}` : null, t);
        // `allowed` from the backend is the cumulative guard ceiling (it
        // includes the tolerance) and must never be shown as free stock, so
        // the free amount is derived here instead.
        const ordered = Number(row.ordered) || 0;
        const allocatedElsewhere = Number(row.allocatedElsewhere) || 0;
        return line.prefix + String(t(`${WARNING_KEY}.cargoOverAllocation`, {
          productName: line.productName,
          attempted: quantity(row.attempted),
          available: quantity(Math.max(ordered - allocatedElsewhere, 0)),
          ordered: quantity(ordered),
          allocatedElsewhere: quantity(allocatedElsewhere),
          tolerance: quantity(CARGO_QUANTITY_TOLERANCE),
        }));
      }).join('\n');
    }
    case 'SUPPLIER_CARGO_OVER_ALLOCATED': {
      const rows = Array.isArray(details.overflow) ? details.overflow : [];
      if (rows.length === 0) return null;
      return rows.map((row: Record<string, unknown>) => {
        const line = overflowLineLabel(row, row.supplierOrderNumber ? String(row.supplierOrderNumber) : null, t);
        return line.prefix + String(t(`${WARNING_KEY}.supplierCargoOverAllocated`, {
          productName: line.productName,
          unit: unit(row.unit),
          attempted: quantity(row.attempted),
          ordered: quantity(row.ordered),
          onOtherTrucks: quantity(row.onOtherTrucks),
          excess: quantity(row.excess),
        }));
      }).join('\n');
    }
    case 'SUPPLIER_LINE_BELOW_TRUCK_CARGO': {
      const lines = Array.isArray(details.lines) ? details.lines : [];
      if (lines.length === 0) return null;
      return lines.map((line: Record<string, unknown>) => String(t(`${WARNING_KEY}.supplierLineBelowTruckCargo`, {
        productName: line.name || '',
        unit: unit(line.unit),
        ordered: quantity(line.ordered),
        onTrucks: quantity(line.onTrucks),
      }))).join('\n');
    }
    case 'TRANSPORT_DELIVERY_EXCEEDS_PLANNED':
      return String(t(`${WARNING_KEY}.transportDeliveryExceedsPlanned`, {
        productName: details.productName,
        unit: unit(details.unit),
        deliveredQuantity: quantity(details.deliveredQuantity),
        plannedQuantity: quantity(details.plannedQuantity),
        difference: quantity(Math.abs(Number(details.difference) || 0)),
      }));
    case 'TRANSPORT_ACTUAL_DELIVERY_BEFORE_LOADING':
      return String(t(`${WARNING_KEY}.transportDeliveryBeforeLoading`, {
        actualDelivery: formatDateEuropean(details.actualDelivery),
        loadingDate: formatDateEuropean(details.loadingDate),
      }));
    case 'WZ_CORRECTED_AFTER_INVOICE': {
      const invoiceNumbers = Array.isArray(details.invoiceNumbers) ? details.invoiceNumbers.filter(Boolean) : [];
      return String(t(`${WARNING_KEY}.wzCorrectedAfterInvoice`, {
        wzNumber: details.wzNumber || '',
        invoicesSuffix: invoiceNumbers.length > 0
          ? String(t(`${WARNING_KEY}.wzCorrectedAfterInvoiceNumbers`, { invoiceNumbers: invoiceNumbers.join(', ') }))
          : '',
      }));
    }
    case 'ORDER_CHANGED_AFTER_INVOICE':
      return String(t(`${WARNING_KEY}.orderChangedAfterInvoice`, {
        orderNumber: details.orderNumber || '',
        invoicesSuffix: afterInvoiceNumbers(details, t),
        changes: afterInvoiceChanges(details, t),
      }));
    case 'SUPPLIER_ORDER_CHANGED_AFTER_INVOICE':
      return String(t(`${WARNING_KEY}.supplierOrderChangedAfterInvoice`, {
        orderNumber: details.orderNumber || '',
        invoicesSuffix: afterInvoiceNumbers(details, t),
        changes: afterInvoiceChanges(details, t),
      }));
    case 'SUPPLIER_ORDER_PZ_QUANTITY_REDUCED': {
      const lines = Array.isArray(details.lines) ? details.lines : [];
      if (lines.length === 0) return null;
      return lines.map((line: Record<string, unknown>) => String(t(`${WARNING_KEY}.supplierOrderPZQuantityReduced`, {
        productName: line.name || '',
        unit: unit(line.unit),
        quantityBefore: quantity(line.quantityBefore),
        quantityAfter: quantity(line.quantityAfter),
      }))).join('\n');
    }
    case 'ORDER_ISSUED_OVER_INVOICE': {
      const invoiceNumbers = Array.isArray(details.invoiceNumbers) ? details.invoiceNumbers.filter(Boolean) : [];
      const lines = Array.isArray(details.lines) ? details.lines : [];
      return String(t(`${WARNING_KEY}.orderIssuedOverInvoice`, {
        invoiceNumbers: invoiceNumbers.join(', '),
        quantities: lines.map((line: Record<string, unknown>) => `${quantity(line.quantity)} ${unit(line.unit)}`).join(', '),
      }));
    }
    // WP15: automatic entries of an approved complaint correction.
    case 'ORDER_COMPLAINT':
    case 'SUPPLIER_ORDER_COMPLAINT': {
      const lines: Array<Record<string, unknown>> = Array.isArray(details.lines) ? details.lines : [];
      const describe = (rows: Array<Record<string, unknown>>, key: string) => rows
        .map((line) => `„${line.name || String(t(`${WARNING_KEY}.afterInvoiceUnnamedLine`))}” ${quantity(line[key])} ${unit(line.unit)}`)
        .join(', ');
      const params = {
        correctionNumber: details.correctionNumber || '',
        invoiceNumber: details.invoiceNumber || '',
      };
      if (warning.code === 'ORDER_COMPLAINT') {
        return String(t(`${WARNING_KEY}.orderComplaint`, { ...params, lines: describe(lines, 'quantity') }));
      }
      const text = String(t(`${WARNING_KEY}.supplierOrderComplaint`, { ...params, lines: describe(lines, 'quantity') }));
      const short = lines.filter((line) => Number(line.shortfallQuantity) > 0);
      return short.length
        ? `${text} ${String(t(`${WARNING_KEY}.supplierOrderComplaintShortfall`, { lines: describe(short, 'shortfallQuantity') }))}`
        : text;
    }
    case 'SUPPLIER_ORDER_DELETED_WITH_DOCUMENTS': {
      const fileNames = Array.isArray(details.fileNames) ? details.fileNames.filter(Boolean) : [];
      return String(t(`${WARNING_KEY}.supplierOrderDeletedWithDocuments`, {
        orderNumber: details.supplierOrderNumber || '',
        fileNames: fileNames.join(', '),
        count: fileNames.length,
      }));
    }
    case 'TRANSPORT_WAREHOUSE_LINKS_REMOVED': {
      const list = (values: unknown) => (Array.isArray(values) ? values.filter(Boolean).join(', ') : '');
      const parts = [
        list(details.wzNumbers) ? String(t(`${WARNING_KEY}.transportLinksWZ`, { numbers: list(details.wzNumbers) })) : '',
        list(details.pzNumbers) ? String(t(`${WARNING_KEY}.transportLinksPZ`, { numbers: list(details.pzNumbers) })) : '',
      ].filter(Boolean).join(', ');
      const truck = list(details.truckSourcedWZNumbers)
        ? String(t(`${WARNING_KEY}.transportLinksTruckSourced`, { numbers: list(details.truckSourcedWZNumbers) }))
        : '';
      return String(t(details.action === 'DELETE'
        ? `${WARNING_KEY}.transportLinksRemovedDelete`
        : `${WARNING_KEY}.transportLinksRemovedUnlink`, {
        documents: parts || String(t(`${WARNING_KEY}.transportLinksNone`)),
        truckSuffix: truck,
      }));
    }
    case 'TRANSPORT_OUTBOUND_WITH_SUPPLIER_ORDERS': {
      const numbers = Array.isArray(details.supplierOrderNumbers) ? details.supplierOrderNumbers.filter(Boolean) : [];
      return String(t(`${WARNING_KEY}.transportOutboundWithSupplierOrders`, { numbers: numbers.join(', ') }));
    }
    case 'TRANSPORT_INBOUND_WITHOUT_SUPPLIER_ORDERS':
      return String(t(`${WARNING_KEY}.transportInboundWithoutSupplierOrders`));
    case 'TRANSPORT_CUSTOMER_PICKUP_ORDER': {
      const numbers = Array.isArray(details.orderNumbers) ? details.orderNumbers.filter(Boolean) : [];
      return String(t(`${WARNING_KEY}.transportCustomerPickupOrder`, { numbers: numbers.join(', ') }));
    }
    case 'ORDER_LINE_WITHDRAWN':
    case 'SUPPLIER_ORDER_LINE_WITHDRAWN': {
      const lines = Array.isArray(details.lines) ? details.lines : [];
      const names = lines.map((line: Record<string, unknown>) => `„${line.name || String(t(`${WARNING_KEY}.afterInvoiceUnnamedLine`))}”`);
      const invoiceNumbers = Array.isArray(details.invoiceNumbers) ? details.invoiceNumbers.filter(Boolean) : [];
      return String(t(warning.code === 'ORDER_LINE_WITHDRAWN'
        ? `${WARNING_KEY}.orderLineWithdrawn`
        : `${WARNING_KEY}.supplierOrderLineWithdrawn`, {
        productNames: names.join(', '),
        invoicesSuffix: invoiceNumbers.length > 0 ? ` (${invoiceNumbers.join(', ')})` : '',
      }));
    }
    case 'ORDER_LINES_REMOVED_FROM_TRANSPORT': {
      const list = (values: unknown) => (Array.isArray(values) ? values.filter(Boolean) : []);
      return String(t(`${WARNING_KEY}.orderLinesRemovedFromTransport`, {
        productNames: list(details.productNames).map((name: unknown) => `„${name}”`).join(', '),
        transports: list(details.transportNumbers).join(', ') || String(t(`${WARNING_KEY}.transportUnnamed`)),
      }));
    }
    case 'ORDER_DELETED_FROM_TRANSPORTS': {
      const list = (values: unknown) => (Array.isArray(values) ? values.filter(Boolean).join(', ') : '');
      const parts = [
        list(details.transportNumbers) ? String(t(`${WARNING_KEY}.orderDeletedTransports`, { numbers: list(details.transportNumbers) })) : '',
        list(details.wzNumbers) ? String(t(`${WARNING_KEY}.orderDeletedWz`, { numbers: list(details.wzNumbers) })) : '',
        ...(details.pendingDraftNumbers || []).map((number: string) => String(t(`${WARNING_KEY}.orderCancelledDraft`, { number }))),
        ...(details.reviewedDraftNumbers || []).map((number: string) => String(t(`${WARNING_KEY}.orderReviewedDraft`, { number }))),
      ].filter(Boolean).join(' i ');
      return String(t(`${WARNING_KEY}.orderDeletedFromTransports`, { orderNumber: details.orderNumber || '', parts }));
    }
    case 'SUPPLIER_ORDER_REMOVED_FROM_TRANSPORTS': {
      const numbers = Array.isArray(details.transportNumbers) ? details.transportNumbers.filter(Boolean).join(', ') : '';
      return String(t(`${WARNING_KEY}.supplierOrderRemovedFromTransports`, {
        orderNumber: details.orderNumber || '',
        numbers: numbers || String(t(`${WARNING_KEY}.transportUnnamed`)),
        action: String(t(`${WARNING_KEY}.supplierOrderRemovedFromTransportsAction.${details.action === 'CANCEL' ? 'CANCEL' : 'DELETE'}`)),
      }));
    }
    case 'ORDER_CANCELLED_WITH_LINKS': {
      const numbers = Array.isArray(details.transportNumbers) ? details.transportNumbers.filter(Boolean).join(', ') : '';
      const parts = [
        numbers ? String(t(`${WARNING_KEY}.orderCancelledTransports`, { numbers })) : '',
        ...(Array.isArray(details.pendingDraftNumbers) ? details.pendingDraftNumbers : [details.draftSupplierOrderNumber].filter(Boolean))
          .map((number: string) => String(t(`${WARNING_KEY}.orderCancelledDraft`, { number }))),
        ...(details.reviewedDraftNumbers || []).map((number: string) => String(t(`${WARNING_KEY}.orderReviewedDraft`, { number }))),
      ].filter(Boolean).join(' i ');
      return String(t(`${WARNING_KEY}.orderCancelledWithLinks`, { orderNumber: details.orderNumber || '', parts }));
    }
    case 'ORDER_ARCHIVED':
    case 'SUPPLIER_ORDER_ARCHIVED': {
      const reasons = (Array.isArray(details.reasons) ? details.reasons : [])
        .map((reason: unknown) => String(t(`${WARNING_KEY}.archiveReason.${reason}`)))
        .join(', ');
      return String(t(warning.code === 'ORDER_ARCHIVED' ? `${WARNING_KEY}.orderArchived` : `${WARNING_KEY}.supplierOrderArchived`, {
        orderNumber: details.orderNumber || '',
        reasons: reasons ? String(t(`${WARNING_KEY}.archiveReasons`, { reasons })) : '',
      }));
    }
    case 'PZ_RECEIVED_WITHOUT_PURCHASE_INVOICE':
      return String(t(`${WARNING_KEY}.pzReceivedWithoutPurchaseInvoice`, { pzNumber: details.pzNumber || '' }));
    case 'PZ_SUPPLIER_ORDER_TRACE_BROKEN': {
      const lines = Array.isArray(details.lines) ? details.lines : [];
      if (lines.length === 0) return null;
      return lines.map((line: Record<string, unknown>) => String(t(`${WARNING_KEY}.pzSupplierOrderTraceBroken`, {
        previousProductName: line.previousProductName || '',
        productName: line.productName || '',
      }))).join('\n');
    }
    case 'PZ_TRANSPORT_ADDED_TO_SUPPLIER_ORDER': {
      const transports = Array.isArray(details.transports) ? details.transports : [];
      const labelOf = (transportId: unknown) => {
        const transport = transports.find((row: Record<string, unknown>) => row.id === transportId);
        return (transport?.label as string) || String(t(`${WARNING_KEY}.transportUnnamed`));
      };
      // Register entry (audit): the cost split of each transport before and
      // after the re-split, written by the backend after the confirmation.
      const shareList = (rows: unknown) => {
        const list = (Array.isArray(rows) ? rows : []).map((row: Record<string, unknown>) => String(t(
          row.kind === 'SUPPLIER_ORDER' ? `${WARNING_KEY}.costShareSupplierOrder` : `${WARNING_KEY}.costShareOrder`,
          { number: row.number || '', percent: percent(row.percent) },
        )));
        return list.length > 0 ? list.join(', ') : String(t(`${WARNING_KEY}.costShareNone`));
      };
      const costShares = (Array.isArray(details.costShares) ? details.costShares : [])
        .map((share: Record<string, unknown>) => String(t(`${WARNING_KEY}.pzTransportCostShares`, {
          transport: labelOf(share.transportId),
          before: shareList(share.before),
          after: shareList(share.after),
        })))
        .join('');
      // WP14: only an INBOUND truck's freight enters the batches; an OUTBOUND
      // one is assigned to the ZD but its cost stays on the customer orders.
      const names = (rows: Array<Record<string, unknown>>) => rows
        .map((transport) => transport.label || String(t(`${WARNING_KEY}.transportUnnamed`)))
        .join(', ');
      const outbound = transports.filter((transport: Record<string, unknown>) => transport.direction === 'OUTBOUND');
      const inbound = transports.filter((transport: Record<string, unknown>) => transport.direction !== 'OUTBOUND');
      const sentences = [
        inbound.length > 0 ? String(t(`${WARNING_KEY}.pzTransportAddedToSupplierOrder`, {
          transports: names(inbound),
          orderNumber: details.supplierOrderNumber || '',
        })) : '',
        outbound.length > 0 ? String(t(`${WARNING_KEY}.pzOutboundTransportAddedToSupplierOrder`, {
          transports: names(outbound),
          orderNumber: details.supplierOrderNumber || '',
        })) : '',
      ].filter(Boolean);
      return sentences.join(' ') + costShares;
    }
    default:
      return null;
  }
}

/**
 * The sentence plus, for a code this build does not know, the support caption
 * carrying the raw code. A frontend older than the backend lands here, so a
 * new warning code never reaches a screen as a bare code.
 */
export function warningCopy(warning: OperationalWarning, t: TFunction): OperationalWarningCopy {
  const known = sentenceForCode(warning, t);
  if (known !== null) return { text: finishSentence(known), supportCode: null };
  if (isReadableSentence(warning.message)) {
    return { text: warning.message.trim(), supportCode: warning.code || null };
  }
  return { text: String(t(`${WARNING_KEY}.unknown`)), supportCode: warning.code || null };
}

/**
 * The confirm button of the shared dialog: „Zapisz mimo ostrzeżeń”, except
 * when every warning is „Gotowe do wydania” with missing goods — then the
 * button says what happens („Oznacz jako gotowe mimo braków”, owner 2026-10-06).
 */
const INVOICE_DOCUMENT_PART_CODES = ['INVOICE_DOCUMENT_PART', 'INVOICE_DOCUMENT_PARTS_EXCEED_TOTAL'];
const onlyCodes = (warnings: OperationalWarning[], codes: string[]) => warnings.length > 0
  && warnings.every((warning) => codes.includes(warning.code));

/** Footer of a reason-free confirmation: where the confirmation is kept. */
export function confirmationNote(warnings: OperationalWarning[], t: TFunction): string {
  return String(t(onlyCodes(warnings, INVOICE_DOCUMENT_PART_CODES)
    ? 'common:operationalException.descriptionInvoiceDocumentPart'
    : 'common:operationalException.descriptionWithoutReason'));
}

export function confirmLabel(warnings: OperationalWarning[], t: TFunction): string {
  if (onlyCodes(warnings, ['ORDER_READY_WITH_MISSING_GOODS'])) return String(t('common:operationalException.continueOrderReadyWithMissingGoods'));
  // Decision 2026-10-27: approving one more part of a document already approved on another order.
  if (onlyCodes(warnings, INVOICE_DOCUMENT_PART_CODES)) {
    const alone = warnings.every((warning) => warning.code === 'INVOICE_DOCUMENT_PARTS_EXCEED_TOTAL'
      && Number(warning.details?.otherParts) === 0);
    return String(t(alone ? 'common:operationalException.continueInvoiceAboveDocumentTotal' : 'common:operationalException.continueInvoiceDocumentPart'));
  }
  return String(t('common:operationalException.continue'));
}

export interface OverflowRow {
  key: string;
  /** „ZK 1/02/2026 · poz. 4” — null when an older warning lacks the line. */
  line: string | null;
  /** Product with its dimensions. */
  product: string;
  ordered: string;
  elsewhere: string;
  attempted: string;
  excess: string;
}

/**
 * Over-allocation warnings as table rows for the confirmation dialog (owner
 * 2026-10-13: the sentences read as a wall of text). One row per order line;
 * null for any other code, which keeps its sentence.
 */
export function overflowRows(warning: OperationalWarning, t: TFunction): { side: 'ZK' | 'ZD'; rows: OverflowRow[] } | null {
  if (warning.code !== 'CARGO_OVER_ALLOCATION' && warning.code !== 'SUPPLIER_CARGO_OVER_ALLOCATED') return null;
  const overflow = Array.isArray(warning.details?.overflow) ? warning.details!.overflow : [];
  if (overflow.length === 0) return null;
  const side = warning.code === 'CARGO_OVER_ALLOCATION' ? 'ZK' : 'ZD';
  const rows = overflow.map((row: Record<string, unknown>, index: number) => {
    const documentLabel = side === 'ZK'
      ? (row.orderNumber ? `ZK ${row.orderNumber}` : null)
      : (row.supplierOrderNumber ? String(row.supplierOrderNumber) : null);
    const lineNumber = Number(row.lineNumber);
    const label = overflowLineLabel(row, documentLabel, t);
    const ordered = Number(row.ordered) || 0;
    const elsewhere = Number(side === 'ZK' ? row.allocatedElsewhere : row.onOtherTrucks) || 0;
    const attempted = Number(row.attempted) || 0;
    const withUnit = (value: number) => `${quantity(value)} ${unit(row.unit || 'pcs')}`;
    return {
      key: `${side}-${String(row.orderProductId || row.supplierOrderItemId || index)}`,
      line: documentLabel && lineNumber > 0 ? `${documentLabel} · poz. ${lineNumber}` : null,
      product: label.productName,
      ordered: withUnit(ordered),
      elsewhere: withUnit(elsewhere),
      attempted: withUnit(attempted),
      excess: `+${withUnit(Math.max(attempted + elsewhere - ordered, 0))}`,
    };
  });
  return { side, rows };
}

/** Sentence only, for callers that render plain text. */
export function warningText(warning: OperationalWarning, t: TFunction): string {
  return warningCopy(warning, t).text;
}
